import { defineComponent as _defineComponent } from 'vue'
import { MeetingsViewMode, MEETINGS_VIEW_MODE_KEY } from '@/@types/Calendar';
import { MeetingMetadata } from '@/@types/Meeting';
import CalendarView from './CalendarView/CalendarView.vue';
import { ArticleEditMode } from '@/@types/ArticleEditMode';
import { DeletedItemStyleClass } from '@/@types/enums/ConfirmationDeleteItem/DeletedItemStyleClass';
import { Meeting } from '@/@types/Meeting';
import { MeetingStatus } from '@/@types/MeetingStatus';
import MyTable from '@/components/layout/MyTable/MyTable.vue';
import MyTableCell from '@/components/layout/MyTable/MyTableCell.vue';
import { PERSONAL_WORKSPACE_TITLE } from '@/constants/SharedConsts';
import EventBus from '@/EventBus';
import { BOT_SETTINGS_UPDATED, OPEN_BOT_SETTINGS } from '@/events';
import { Article } from '@/models/article/Article';
import { BotConfig } from '@/models/forms/BotConfig';
import { Workspace } from '@/models/Workspace';
import logger from '@/other/Logger';
import { DRAFT_EDIT } from '@/routerNames';
import apiService from '@/services/ApiService';
import confirmationService from '@/services/ConfirmationService';
import notificationService from '@/services/NotificationService';
import urlService from '@/services/UrlService';
import {
  dispatchGetDraftsCount,
  dispatchGetMeetingsCount,
  dispatchGetPersonalArticlesCount,
} from '@/store/dispatchers/uiDispatchers';
import { dispatchOpenArticleEditor } from '@/store/dispatchers/articleEditorDispatchers';
import { Loading } from 'element-ui';
import { debounce } from 'lodash';
import slugify from 'slugify';
import { onBeforeMount, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router/composables';


export default /*#__PURE__*/_defineComponent({
  __name: 'MeetingsPage',
  setup(__props) {

const router = useRouter();

const tableColumns = [
  { label: 'TITLE' },
  { label: 'WORKSPACE' },
  { label: 'ORGANIZED BY' },
  { label: 'MEETING TIME', sortBy: ['startAt'] },
  { label: 'STATUS' },
  { label: '' },
];

const meetings = ref<Meeting[]>([]);
const organizers = ref<string[]>([]);
const workspaces = ref<Pick<Workspace, 'id' | 'title'>[]>([]);

const storedViewMode = localStorage.getItem(MEETINGS_VIEW_MODE_KEY);
const validViewModes: MeetingsViewMode[] = ['list', 'calendar'];
const viewMode = ref<MeetingsViewMode>(
  storedViewMode && validViewModes.includes(storedViewMode as MeetingsViewMode)
    ? (storedViewMode as MeetingsViewMode)
    : 'list'
);

const setViewMode = async (mode: MeetingsViewMode) => {
  viewMode.value = mode;
  localStorage.setItem(MEETINGS_VIEW_MODE_KEY, mode);
  // Refresh meetings list when switching to list view (data may have changed in calendar)
  if (mode === 'list') {
    try {
      await loadMeetings();
    } catch (e) {
      await apiService.handleResponseError(e);
    }
  }
};

const isFailureDialogOpen = ref(false);
const selectedFailureData = ref<MeetingMetadata['failureData'] | null>(null);

// Sorting
const sortingByStart = ref<number>(1);

// Filters
const searchQuery = ref('');
const searchWorkspace = ref('');
const searchOrganizer = ref('');
const searchStatus = ref('');

// Pagination
const itemPerPage = 10;
const totalPages = ref(0);
const page = ref(1);

const isFailed = (status: MeetingStatus) => {
  return status && status === MeetingStatus.FAILED;
};

const isUpcoming = (status: MeetingStatus) => {
  return status && status === MeetingStatus.UPCOMING;
};

const isAllowEdit = (status: MeetingStatus) => {
  return status === MeetingStatus.FINISHED;
};

const isRecurringMeeting = (item: Meeting): boolean => {
  return !!(item?.recurrence?.length || item?.mergedOccurrences?.length);
};

const getWorkspaceTitle = (title: string) => {
  return title?.indexOf('PW_') == 0 ? PERSONAL_WORKSPACE_TITLE : title;
};

const getStatus = (status: MeetingStatus) => {
  switch (status) {
    case MeetingStatus.UPCOMING:
      return 'Upcoming';
    case MeetingStatus.PROCESSING:
      return 'Processing';
    case MeetingStatus.NO_SHOW:
      return 'No-show';
    case MeetingStatus.FINISHED:
      return 'Finished';
    case MeetingStatus.FAILED:
      return 'Failed';
    case MeetingStatus.SKIPPED:
      return 'Skipped';
  }
};

const getClientTime = (time: Date) => {
  const localDate = new Date(time);

  const day = localDate.getDate();
  const month = localDate.getMonth() + 1; // Month is 0-based
  const year = localDate.getFullYear();

  let hours = localDate.getHours();
  const minutes = localDate.getMinutes().toString().padStart(2, '0');
  const amPm = hours >= 12 ? 'PM' : 'AM';

  hours = hours % 12 || 12; // Convert 0 (midnight) to 12 AM, and 13-23 to 1-11 PM

  return `${day}/${month}/${year} at ${hours}:${minutes} ${amPm}`;
};

const openBotSettingsDialog = (item?: Meeting) => {
  return EventBus.$emit(OPEN_BOT_SETTINGS, item);
};

const showFailureDetails = (meeting: Meeting) => {
  selectedFailureData.value = meeting.metadata?.failureData || null;
  isFailureDialogOpen.value = true;
};

const handleSortChange = (e: { sortBy: string[] }) => {
  if (e.sortBy.includes('startAt')) {
    sortingByStart.value *= -1; // Toggle sorting direction

    const sorted = sortMeetings([...meetings.value], sortingByStart.value);
    meetings.value = sorted; // Ensure reactivity
  }
};

const sortMeetings = (items: Meeting[], direction: number): Meeting[] => {
  return items.sort((a, b) => {
    // Define priority based on direction
    const getStatusPriority = (status: string) => {
      if (direction === -1) return status === 'finished' ? 0 : 1; // Lower priority value means higher sorting priority
      if (direction === 1) return status === 'upcoming' ? 0 : 1;
      return 1;
    };

    // Compare status priority first
    const statusPriority = getStatusPriority(a.status) - getStatusPriority(b.status);
    if (statusPriority !== 0) return statusPriority;

    // If same priority, sort by startAt
    return direction * (new Date(a.startAt).getTime() - new Date(b.startAt).getTime());
  });
};

const handleSearchInput = debounce(async () => {
  const loader = Loading.service({ lock: true, text: 'Searching' });

  page.value = 1;

  try {
    await loadMeetings();
  } catch (e) {
    await apiService.handleResponseError(e);
  } finally {
    loader.close();
    loader.$destroy();
  }
}, 500);

const handleClickOnRow = async ({ item, isEdit = false }: { item: Meeting; isEdit: boolean }) => {
  if (item?.status !== MeetingStatus.FINISHED || !item?.article?.id) return;
  const loader = Loading.service({ lock: true, text: 'Opening' });

  try {
    // If it's draft - always open edit mode
    if (item?.isDraft) {
      await router.push({ name: DRAFT_EDIT, params: { id: item?.article?.id } });
    }

    if (!item?.isDraft) {
      if (isEdit) {
        await dispatchOpenArticleEditor(item?.article?.id);
      } else {
        if (!item.article.slug || !item.article.shortId || !item.workspaceShortId) {
          return notificationService.warning('Not enough data to open article');
        }
        const article: Partial<Article> = {
          id: item.article.id,
          slug: slugify(item.article.slug),
          shortId: item.article.shortId,
        };
        const workspace: Partial<Workspace> = {
          shortId: item.workspaceShortId,
          title: item.workspace.toLowerCase(),
        };
        await router.push(urlService.getArticleViewRoute(article, workspace, ArticleEditMode.VIDEO));
      }
    }
  } catch (e) {
    logger.info('Failed to edit meeting');
  } finally {
    loader.close();
    loader.$destroy();
  }
};

const handleUpdatePage = async (p: number) => {
  page.value = p;

  const loader = Loading.service({ lock: true, text: 'Loading meetings' });

  try {
    await loadMeetings();
  } catch (e) {
    await apiService.handleResponseError(e);
  } finally {
    loader.close();
    loader.$destroy();
  }
};

const handleDeleteMeeting = async (item: Meeting, all = true) => {
  const result = await confirmationService
    .showRemovePopup(item.title, null, DeletedItemStyleClass.MEETING)
    .catch(() => 'cancel');

  if (result !== 'confirm') return;

  const loader = Loading.service({ lock: true, text: 'Deleting meeting' });

  try {
    // For merged recurring events, get the earliest occurrence ID
    const meetingIdToDelete = item.mergedOccurrences?.length
      ? item.mergedOccurrences[0].id // Delete earliest occurrence first
      : item.id || item._id;

    await apiService.meetings.delete(meetingIdToDelete!, item.status, item.recurrence, all, item.mergedOccurrences);

    await updateCounts();

    // Single delete on a merged recurring item: shift off the deleted occurrence
    // and show the next one in its place. No BE response needed — we already have
    // the full list in mergedOccurrences.
    if (!all && item.mergedOccurrences && item.mergedOccurrences.length > 1) {
      const remaining = item.mergedOccurrences.slice(1);
      item.id = remaining[0].id;
      item.startAt = remaining[0].startAt;
      item.mergedOccurrences = remaining;
      return;
    }

    if (all && item.recurringEventId) {
      // "Delete all occurrences": remove every item in the same recurring series
      const seriesId = item.recurringEventId;
      meetings.value = meetings.value.filter((m) => m.recurringEventId !== seriesId);
    } else {
      // Remove single meeting from list (last occurrence or non-recurring event)
      const id = item.id ? item.id : item._id;
      meetings.value = meetings.value.filter((m) => (m.id ? m.id : m._id) !== id);
    }
  } catch (e) {
    await apiService.handleResponseError(e);
  } finally {
    loader.close();
    loader.$destroy();
  }
};

const handleFilterChange = async () => {
  const loader = Loading.service({ lock: true, text: 'Searching' });

  page.value = 1;

  try {
    await loadMeetings();
  } catch (e) {
    await apiService.handleResponseError(e);
  } finally {
    loader.close();
    loader.$destroy();
  }
};

const updateCounts = async () => {
  await Promise.all([dispatchGetDraftsCount(), dispatchGetMeetingsCount(), dispatchGetPersonalArticlesCount()]);
};

const loadFiltersData = async () => {
  const resFiltersData = await apiService.meetings.getFiltersData();
  const data = resFiltersData.data.filters;
  organizers.value = [...new Set(data.map((item) => item.organizedBy))];
  workspaces.value = [...new Map(data.map((item) => [item.workspace.id, item.workspace])).values()].filter(
    (w) => w?.title?.length
  );
};

const loadMeetings = async () => {
  const resMeetings = await apiService.meetings.getAll({
    page: page.value,
    q: searchQuery.value.length ? searchQuery.value : undefined,
    status: searchStatus.value.length ? searchStatus.value : undefined,
    workspace: searchWorkspace.value.length ? searchWorkspace.value : undefined,
    organizer: searchOrganizer.value.length ? searchOrganizer.value : undefined,
  });

  meetings.value = resMeetings.data.meetings;
  totalPages.value = Math.ceil(resMeetings.data.count / itemPerPage);
};

const onBotConfigUpdate = async ({
  config,
  workspaces,
}: {
  config: BotConfig;
  workspaces: Pick<Workspace, 'id' | 'title'>[];
}) => {
  const isIndividualConfig = !!config?.eventId;

  // General (default) config change affects every meeting that doesn't have an individual
  // override — including rows in "processing" status, whose workspace is resolved by the
  // ecs_bots pipeline on the server. We can't replay that resolution locally, so we do a
  // full re-fetch to pull the correct workspace for each row (SNAP-2177).
  if (!isIndividualConfig) {
    await loadMeetings();
    await updateCounts();
    return;
  }

  // Individual (per-event) config change — the "Meeting settings" row menu item is only
  // available for upcoming meetings (see template: v-if="isUpcoming(item?.status)"), so
  // we only ever need to update an upcoming row locally. Match both a single occurrence
  // (by `id`) and every occurrence of a recurring series (by `recurringEventId`).
  meetings.value = meetings.value.map((m) => {
    if (m.status !== MeetingStatus.UPCOMING) return m;
    const matchesEvent = config.eventId === m.id || config.eventId === m.recurringEventId;
    if (!matchesEvent) return m;

    const isWorkspaceChanged = config.workspaceId !== m.workspaceId;
    if (!isWorkspaceChanged) return m;

    const matchedWorkspace = workspaces.find((w) => w.id === config.workspaceId);
    const newWorkspaceTitle = matchedWorkspace ? getWorkspaceTitle(matchedWorkspace.title) : '-';
    const workspaceId = matchedWorkspace ? matchedWorkspace.id : m.workspaceId;
    return { ...m, workspace: newWorkspaceTitle, workspaceId };
  });
  await updateCounts();
};

onMounted(async () => {
  const loader = Loading.service({ lock: true, text: 'Loading meetings' });

  try {
    await loadMeetings();
    await loadFiltersData();
  } catch (e) {
    await apiService.handleResponseError(e);
  } finally {
    loader.close();
    loader.$destroy();
  }
});

onBeforeMount(async () => {
  EventBus.$on(BOT_SETTINGS_UPDATED, onBotConfigUpdate);
});

onBeforeUnmount(() => {
  EventBus.$off(BOT_SETTINGS_UPDATED, onBotConfigUpdate);
});

watch([searchWorkspace, searchOrganizer, searchStatus], async () => {
  await handleFilterChange();
});

return { __sfc: true,router, tableColumns, meetings, organizers, workspaces, storedViewMode, validViewModes, viewMode, setViewMode, isFailureDialogOpen, selectedFailureData, sortingByStart, searchQuery, searchWorkspace, searchOrganizer, searchStatus, itemPerPage, totalPages, page, isFailed, isUpcoming, isAllowEdit, isRecurringMeeting, getWorkspaceTitle, getStatus, getClientTime, openBotSettingsDialog, showFailureDetails, handleSortChange, sortMeetings, handleSearchInput, handleClickOnRow, handleUpdatePage, handleDeleteMeeting, handleFilterChange, updateCounts, loadFiltersData, loadMeetings, onBotConfigUpdate, CalendarView, MyTable, MyTableCell }
}

})